import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { subscribeMyNotifications } from '../../features/notifications/api/subscribeMyNotifications.js';
import {
  BLOCKING_CATEGORIES,
  NotificationStatus,
  type UserNotification,
} from '../../features/notifications/db/UserNotification.js';

/**
 * Counter for the sidebar entry. Deliberately its own stateful component so the app shell does
 * not have to carry a subscription — and so importing it never pulls the whole notifications
 * page into the shell bundle.
 *
 * It counts what actually needs this person, not what is unread. Two things qualify: a
 * question that blocks an offer from going to SAP, and a free-text answer whose reading is
 * waiting for a yes. Everything else — an assumption to look over, a gap for the customer to
 * close — is real work but not urgent, and putting it in the badge would make the badge
 * permanent and therefore meaningless.
 *
 * `watchQuery` yields documents, not an aggregate, so the count is taken on the client. At
 * these volumes that is entirely sufficient and needs no new framework code.
 */
const SELECTION_SET = {
  _id: 1,
  category: 1,
  status: 1,
  answer: { _id: 1, interpretation: { _id: 1, needsConfirmation: 1 } },
} as const;

const needsAction = (notification: UserNotification) =>
  (BLOCKING_CATEGORIES.includes(notification.category) && notification.status === NotificationStatus.OPEN) ||
  !!notification.answer?.interpretation?.needsConfirmation;

export const NotificationsBadge = stateful(() => {
  const { value } = useQuery(() => subscribeMyNotifications(false, 0, 200)(SELECTION_SET), []);
  const count = (value ?? []).filter(needsAction).length;
  if (!count) return nothing;
  return html`<span class="badge badge-sm badge-error shrink-0" title="Blockierte Anfragen und offene Bestätigungen">
    ${count > 99 ? '99+' : count}
  </span>`;
});
